import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsService } from '../../services/settingsService';
import { formatCurrency } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, BanknotesIcon } from '@heroicons/react/24/outline';

const DepositSchemes = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [formData, setFormData] = useState({
    scheme_code: '',
    scheme_name: '',
    scheme_type: 'daily_savings',
    minimum_deposit: '',
    maximum_deposit: '',
    interest_rate: '',
    interest_type: 'compound',
    compound_frequency: 'yearly',
    duration_months: '',
    premature_withdrawal_allowed: false,
    premature_withdrawal_penalty: '',
    lock_in_period_months: '',
    minimum_balance: '',
    is_active: true
  });

  const { data, isLoading } = useQuery(
    'deposit-schemes',
    () => settingsService.getDepositSchemes()
  );

  const createMutation = useMutation(
    (schemeData) => settingsService.createDepositScheme(schemeData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('deposit-schemes');
        setShowModal(false);
        resetForm();
        toast.success('Deposit scheme created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Creation failed');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => settingsService.updateDepositScheme(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('deposit-schemes');
        setShowModal(false);
        resetForm();
        toast.success('Deposit scheme updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Update failed');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => settingsService.deleteDepositScheme(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('deposit-schemes');
        toast.success('Deposit scheme deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Delete failed');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      scheme_code: '',
      scheme_name: '',
      scheme_type: 'daily_savings',
      minimum_deposit: '',
      maximum_deposit: '',
      interest_rate: '',
      interest_type: 'compound',
      compound_frequency: 'yearly',
      duration_months: '',
      premature_withdrawal_allowed: false,
      premature_withdrawal_penalty: '',
      lock_in_period_months: '',
      minimum_balance: '',
      is_active: true
    });
    setEditingScheme(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      minimum_deposit: parseFloat(formData.minimum_deposit),
      maximum_deposit: formData.maximum_deposit ? parseFloat(formData.maximum_deposit) : null,
      interest_rate: parseFloat(formData.interest_rate),
      duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
      premature_withdrawal_penalty: parseFloat(formData.premature_withdrawal_penalty) || 0,
      lock_in_period_months: parseInt(formData.lock_in_period_months) || 0,
      minimum_balance: parseFloat(formData.minimum_balance) || 0
    };

    if (editingScheme) {
      updateMutation.mutate({ id: editingScheme.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (scheme) => {
    setEditingScheme(scheme);
    setFormData({
      scheme_code: scheme.scheme_code,
      scheme_name: scheme.scheme_name,
      scheme_type: scheme.scheme_type,
      minimum_deposit: scheme.minimum_deposit,
      maximum_deposit: scheme.maximum_deposit || '',
      interest_rate: scheme.interest_rate,
      interest_type: scheme.interest_type,
      compound_frequency: scheme.compound_frequency || 'yearly',
      duration_months: scheme.duration_months || '',
      premature_withdrawal_allowed: scheme.premature_withdrawal_allowed,
      premature_withdrawal_penalty: scheme.premature_withdrawal_penalty || '',
      lock_in_period_months: scheme.lock_in_period_months || '',
      minimum_balance: scheme.minimum_balance || '',
      is_active: scheme.is_active
    });
    setShowModal(true);
  };

  const handleDelete = (scheme) => {
    if (window.confirm(`Are you sure you want to delete scheme "${scheme.scheme_name}"?`)) {
      deleteMutation.mutate(scheme.id);
    }
  };

  const schemes = data?.data || [];

  const schemeTypes = {
    'daily_savings': 'Daily Savings',
    'weekly_savings': 'Weekly Savings',
    'monthly_savings': 'Monthly Savings',
    'dps': 'DPS (Deposit Pension Scheme)',
    'fdr': 'FDR (Fixed Deposit)',
    'special': 'Special Scheme'
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Deposit Schemes</h2>
          <p className="text-gray-600">Manage savings and deposit products</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Scheme
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : schemes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No deposit schemes found</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Scheme
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schemes.map((scheme) => (
            <div key={scheme.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{scheme.scheme_name}</h3>
                  <p className="text-sm text-gray-500">{scheme.scheme_code}</p>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleEdit(scheme)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(scheme)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium capitalize">{schemeTypes[scheme.scheme_type] || scheme.scheme_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Interest Rate:</span>
                  <span className="font-medium text-green-600">{scheme.interest_rate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Min Deposit:</span>
                  <span>{formatCurrency(scheme.minimum_deposit)}</span>
                </div>
                {scheme.maximum_deposit && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Deposit:</span>
                    <span>{formatCurrency(scheme.maximum_deposit)}</span>
                  </div>
                )}
                {scheme.duration_months && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span>{scheme.duration_months} months</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${scheme.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {scheme.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scheme Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingScheme ? 'Edit Deposit Scheme' : 'Add New Deposit Scheme'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Scheme Code *</label>
                  <input
                    type="text"
                    value={formData.scheme_code}
                    onChange={(e) => setFormData({ ...formData, scheme_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    disabled={!!editingScheme}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scheme Name *</label>
                  <input
                    type="text"
                    value={formData.scheme_name}
                    onChange={(e) => setFormData({ ...formData, scheme_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scheme Type *</label>
                  <select
                    value={formData.scheme_type}
                    onChange={(e) => setFormData({ ...formData, scheme_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="daily_savings">Daily Savings</option>
                    <option value="weekly_savings">Weekly Savings</option>
                    <option value="monthly_savings">Monthly Savings</option>
                    <option value="dps">DPS (Deposit Pension Scheme)</option>
                    <option value="fdr">FDR (Fixed Deposit)</option>
                    <option value="special">Special Scheme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Interest Rate (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Interest Type</label>
                  <select
                    value={formData.interest_type}
                    onChange={(e) => setFormData({ ...formData, interest_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="flat">Flat Rate</option>
                    <option value="reducing">Reducing Balance</option>
                    <option value="compound">Compound</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Compound Frequency</label>
                  <select
                    value={formData.compound_frequency}
                    onChange={(e) => setFormData({ ...formData, compound_frequency: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    disabled={formData.interest_type !== 'compound'}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half_yearly">Half Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Minimum Deposit *</label>
                  <input
                    type="number"
                    value={formData.minimum_deposit}
                    onChange={(e) => setFormData({ ...formData, minimum_deposit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Maximum Deposit</label>
                  <input
                    type="number"
                    value={formData.maximum_deposit}
                    onChange={(e) => setFormData({ ...formData, maximum_deposit: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Duration (months)</label>
                  <input
                    type="number"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="For DPS/FDR"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lock-in Period (months)</label>
                  <input
                    type="number"
                    value={formData.lock_in_period_months}
                    onChange={(e) => setFormData({ ...formData, lock_in_period_months: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Minimum Balance</label>
                  <input
                    type="number"
                    value={formData.minimum_balance}
                    onChange={(e) => setFormData({ ...formData, minimum_balance: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Premature Withdrawal Penalty (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.premature_withdrawal_penalty}
                    onChange={(e) => setFormData({ ...formData, premature_withdrawal_penalty: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.premature_withdrawal_allowed}
                      onChange={(e) => setFormData({ ...formData, premature_withdrawal_allowed: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Allow Premature Withdrawal</span>
                  </label>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingScheme ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositSchemes;